import { useEffect, useMemo, useRef, useState } from 'react'
import { deliverMaterialAssets, readMaterialMessage, wrapMaterialHtml } from '@aurora/core'
import type { InteractiveResult, ItemEvent, ItemMemory, MaterialAsset } from '@aurora/core'

/**
 * A generated material, running.
 *
 * SAFETY, stated plainly: the document is model-authored, so it runs in an
 * iframe with `sandbox="allow-scripts"` and nothing else — a separate origin
 * with no access to this page, its storage, or its cookies — under a CSP that
 * forbids every network request. The only way out is `postMessage`, and only
 * messages from this frame's own window are read, through one validator the
 * phone shares.
 *
 * The CSP does not govern navigation, and the sandbox lets a frame navigate
 * itself, so a material could set location.href. A browser cannot stop that
 * one request, but the frame must not stay pointed at the outside page: a
 * second load of one document means it left, and so does the kit's
 * 'sapling:gone' (sent on pagehide, which also covers leaving before the
 * first load). Either way the frame is taken down, nothing more goes in, and
 * nothing more is read. The landing page keeps the sandbox's opaque origin,
 * so the moment before that it can post what the material itself could have
 * posted, no more. The phone blocks the navigation outright.
 */
export function MaterialFrame({ html, theme, carry, memory, assets, onResult, onPartial, onProgress, onReady, className }: {
  html: string
  theme: 'light' | 'dark'
  /** What the material saved last time. Read once per launch. */
  carry?: unknown
  /** Her memory of every item, by key. Read once per launch, like carry. */
  memory?: Record<string, ItemMemory>
  /** The media made for it (artifact.assets). Ones that finish while she plays are handed in then. */
  assets?: MaterialAsset[]
  onResult: (result: InteractiveResult) => void
  /** She left before the end, after the material saved something: log it, nothing more. */
  onPartial?: (result: InteractiveResult) => void
  onProgress?: (progress: { step: number; of: number }) => void
  /** The material has drawn its first screen: whatever covered the wait can go. */
  onReady?: () => void
  className?: string
}) {
  const frame = useRef<HTMLIFrameElement>(null)
  // The saved state is read at launch only: a save during play must not
  // rebuild the document under her finger.
  const launchCarry = useRef(carry)
  const launchMemory = useRef(memory)
  const launchAssets = useRef(assets)
  const srcdoc = useMemo(() => wrapMaterialHtml(html, theme, { carry: launchCarry.current, memory: launchMemory.current, assets: launchAssets.current }), [html, theme])
  // Every attempt she makes, kept until the material finishes or she leaves.
  const events = useRef<ItemEvent[]>([])
  const saved = useRef<unknown>(undefined)
  const started = useRef(Date.now())
  const finished = useRef(false)
  const latest = useRef({ onResult, onPartial, onProgress, onReady })
  latest.current = { onResult, onPartial, onProgress, onReady }

  // Media goes in by hand: the frame has no network, so this page fetches
  // each ready file and transfers its bytes in once the material is ready.
  const ready = useRef(false)
  // The document that has loaded. Its first load is the material; a second
  // load of the same document is the material leaving. A new document (the
  // theme changed) is allowed its own first load.
  const loaded = useRef<string | null>(null)
  const current = useRef(srcdoc)
  current.current = srcdoc
  const [escaped, setEscaped] = useState(false)
  const gone = useRef(false)
  const leave = () => {
    ready.current = false
    gone.current = true
    setEscaped(true)
  }
  const onLoad = () => {
    if (loaded.current !== srcdoc) { loaded.current = srcdoc; return }
    leave()
  }
  const sentAssets = useRef(new Set<string>())
  const latestAssets = useRef(assets)
  latestAssets.current = assets
  const deliver = () => {
    const win = frame.current?.contentWindow
    if (!ready.current || gone.current || !win) return
    deliverMaterialAssets(latestAssets.current, sentAssets.current,
      (url) => fetch(url).then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(String(r.status))))),
      (msg) => win.postMessage(msg, '*', 'data' in msg && msg.data instanceof ArrayBuffer ? [msg.data] : []))
  }
  useEffect(deliver, [assets]) // eslint-disable-line react-hooks/exhaustive-deps
  // A new document starts empty-handed: everything goes in again after its ready.
  useEffect(() => { ready.current = false; sentAssets.current = new Set() }, [srcdoc])

  // Leaving partway still keeps where she got to: whatever the material
  // saved is handed over as an unfinished result when the frame goes away.
  useEffect(() => () => {
    if ((saved.current === undefined && !events.current.length) || finished.current) return
    const now = Date.now()
    latest.current.onPartial?.({
      specKind: 'html' as any, startedAt: started.current, completedAt: now, durationMs: now - started.current,
      completed: false, carry: saved.current, events: events.current,
    })
  }, [])

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (gone.current || !frame.current || e.source !== frame.current.contentWindow || e.origin !== 'null') return
      const msg = readMaterialMessage(e.data)
      if (!msg) return
      // The old document says goodbye when the theme swaps it too, so a
      // goodbye while a new document is on its way is not the material leaving.
      if (msg.type === 'sapling:gone') { if (loaded.current === null || loaded.current === current.current) leave(); return }
      if (msg.type === 'sapling:ready') { ready.current = true; deliver(); latest.current.onReady?.() }
      if (msg.type === 'sapling:save') saved.current = msg.carry
      if (msg.type === 'sapling:record') events.current.push(msg.event)
      if (msg.type === 'sapling:result') {
        finished.current = true
        latest.current.onResult({ ...msg.result, ...(saved.current === undefined ? {} : { carry: saved.current }), events: events.current })
      }
      if (msg.type === 'sapling:progress') latest.current.onProgress?.({ step: msg.step, of: msg.of })
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])

  // A material that navigated away is not ours any more: nothing is left on screen to post to us.
  if (escaped) return <div className={className ?? 'material-frame'} aria-hidden="true" />

  return (
    <iframe
      onLoad={onLoad}
      ref={frame}
      className={className ?? 'material-frame'}
      title="Activity"
      sandbox="allow-scripts"
      srcDoc={srcdoc}
      referrerPolicy="no-referrer"
      allow="autoplay"
    />
  )
}
